import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard } from 'lucide-react';

const API_BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app';

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasRefAccess, setHasRefAccess] = useState(false);
  const navigate = useNavigate();

  // Check if user is logged in + check super admin role
  useEffect(() => {
    const checkAuthStatus = () => {
      const authToken = localStorage.getItem('authToken');
      const userData  = localStorage.getItem('userData');

      if (authToken || userData) {
        setIsLoggedIn(true);

        // ── Check if super admin ─────────────────────────────────
        try {
          const user = JSON.parse(userData);
          if (user && (user.role === 'super_admin' || user.email === 'info@easylearn.net.in')) {
            setIsSuperAdmin(true);
            setHasRefAccess(true); // super admin always has access
          } else {
            setIsSuperAdmin(false);
            // ── Read refAccess from localStorage ──
            const refAccess = localStorage.getItem('refAccess');
            setHasRefAccess(refAccess === '1');
          }
        } catch {
          setIsSuperAdmin(false);
          setHasRefAccess(false);
        }
      } else {
        setIsLoggedIn(false);
        setIsSuperAdmin(false);
        setHasRefAccess(false);
      }
    };

    checkAuthStatus();

    // ✅ BUG FIX #2 & #3 — Poll backend every 10s to check if reference access changed.
    // This ensures that when super admin hides/unhides reference for a user,
    // that user's navbar updates within 10 seconds without needing a page reload.
    const pollRefAccess = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) return;
        const user = JSON.parse(userData);
        // Skip polling for super admin — they always have access
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;

        const res = await fetch(
          `${API_BASE_URL}/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        const oldVal = localStorage.getItem('refAccess');

        if (newVal !== oldVal) {
          // Access state changed — update localStorage and React state immediately
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {
        // Silently ignore network errors during polling
      }
    };

    // ✅ Also listen for the cross-tab storage event fired by Superadmindashboard.js
    // when admin toggles access (catches same-browser cross-tab updates instantly)
    const handleStorageEvent = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            if (user.email === email) {
              localStorage.setItem('refAccess', value);
              setHasRefAccess(value === '1');
            }
          }
        } catch {}
      }
      // Also re-run full auth check on any storage change
      checkAuthStatus();
    };

    const pollInterval = setInterval(pollRefAccess, 10000); // poll every 10 seconds

    window.addEventListener('storage',         handleStorageEvent);
    window.addEventListener('authChange',      checkAuthStatus);
    window.addEventListener('refAccessChange', checkAuthStatus);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage',         handleStorageEvent);
      window.removeEventListener('authChange',      checkAuthStatus);
      window.removeEventListener('refAccessChange', checkAuthStatus);
    };
  }, []);

  const handleLogout = () => {
    // ── Call backend to mark session as closed ───────────────────
    const sessionEmail = localStorage.getItem('sessionEmail');
    if (sessionEmail) {
      navigator.sendBeacon(
        `${API_BASE_URL}/isfathena/session/logout`,
        new URLSearchParams({ email: sessionEmail })
      );
      // Stop heartbeat interval
      if (window._sessionInterval) {
        clearInterval(window._sessionInterval);
        window._sessionInterval = null;
      }
    }

    // ── Clear all localStorage auth keys ────────────────────────
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('sessionEmail');
    localStorage.removeItem('refAccess');

    setIsLoggedIn(false);
    setIsSuperAdmin(false);
    setHasRefAccess(false);

    window.dispatchEvent(new Event('authChange'));
    navigate('/login');
  };

  // ── Super Admin Desktop Navbar (special full-width layout) ────────────────
  if (isSuperAdmin) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950 border-b border-orange-500/20"
           style={{ backdropFilter: 'blur(12px)', background: 'rgba(10,10,15,0.97)' }}>
        <div className="w-full px-6 lg:px-10">
          <div className="flex items-center h-16 gap-6">

            {/* Logo — far left */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/Assests/isf_acedumy_logo.png`}
                alt="ISF Academy Logo"
                className="h-11 w-11 object-contain"
              />
              <h1 className="text-2xl font-bold text-white cursor-pointer" onClick={() => navigate('/')}>
                ISF<span className="text-orange-500"> Athena</span>
              </h1>
            </div>

            {/* Nav links — centered, take up remaining space */}
            <div className="hidden md:flex flex-1 items-center justify-center gap-6">
              <a href="/standard" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Standards</a>
              <a href="/products" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Products</a>
              {hasRefAccess && (
                <a href="/reffrence" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Reference</a>
              )}
              <a href="/courseware" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Courseware</a>
              <a href="/marketing" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Marketing Models</a>
              <a href="/join_and_earn" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Join &amp; Earn</a>
              <a href="/workflow" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Automation </a>
              <a href="/coming-soon" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">E-Commerce</a>
              <a href="/contact" className="text-gray-300 hover:text-orange-400 transition-colors text-sm font-medium tracking-wide">Contact</a>
            </div>

            {/* Admin Dashboard + Logout — far right */}
            <div className="hidden md:flex items-center flex-shrink-0 gap-3">
              <a
                href="/Superadmindashboard"
                className="flex items-center gap-1.5 text-orange-400 hover:text-orange-300 transition-colors text-sm font-semibold tracking-wide border border-orange-500/40 px-3 py-1 rounded-full hover:border-orange-400"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                Admin Dashboard
              </a>

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  color: '#fff',
                  boxShadow: '0 0 16px rgba(220,38,38,0.35)',
                  letterSpacing: '0.03em',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 24px rgba(220,38,38,0.6)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 16px rgba(220,38,38,0.35)'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
                </svg>
                Logout
              </button>
            </div>

            {/* Mobile menu button (super admin) */}
            <div className="md:hidden ml-auto">
              <button onClick={() => setIsOpen(!isOpen)} className="text-gray-300 hover:text-white focus:outline-none">
                <svg className="h-6 w-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  {isOpen ? <path d="M6 18L18 6M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu (super admin) */}
        {isOpen && (
          <div className="md:hidden bg-gray-950 border-t border-orange-500/20">
            <div className="px-4 pt-2 pb-4 space-y-2">
              <a href="/Superadmindashboard" className="flex items-center gap-2 px-3 py-2 text-orange-400 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 rounded-md font-semibold transition">
                <LayoutDashboard className="w-4 h-4" /> Admin Dashboard
              </a>
              <a href="/standard" className="block px-3 py-2 text-white hover:bg-gray-800 rounded-md font-medium">Standards</a>
              <a href="/products" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Products</a>
              {hasRefAccess && (
                <a href="/reffrence" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Reference</a>
              )}
              <a href="/courseware" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Courseware</a>
              <a href="/marketing" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Marketing Models</a>
              <a href="/join_and_earn" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Join &amp; Earn</a>
              <a href="/workflow" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Automation</a>
              <a href="/ComingSoon" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">E-Commerce </a>
              <a href="/contact" className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium">Contact</a>
              <button onClick={handleLogout} className="w-full mt-4 px-6 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 transition">Logout</button>
            </div>
          </div>
        )}
      </nav>
    );
  }

  // ── Regular / Normal User Navbar ────────────────────────────────────────────
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <img
              src={`${process.env.PUBLIC_URL}/Assests/isf_acedumy_logo.png`}
              alt="ISF Academy Logo"
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-2xl font-bold text-white cursor-pointer" onClick={() => navigate('/')}>
              ISF<span className="text-orange-500"> Athena</span>
            </h1>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:block ml-2">
            <div className="flex items-center space-x-8">
              <a href="/standard" className="text-white hover:text-orange-500 transition font-medium">
                Standards
              </a>

              <a href="/products" className="text-gray-300 hover:text-orange-500 transition font-medium">
                Products
              </a>

              {isLoggedIn && hasRefAccess && (
                <a href="/reffrence" className="text-gray-300 hover:text-orange-500 transition font-medium">
                  Reference
                </a>
              )}

              {isLoggedIn && (
                <a href="/courseware" className="text-gray-300 hover:text-orange-500 transition font-medium">
                  Courseware
                </a>
              )}

              <a href="/marketing" className="text-gray-300 hover:text-orange-500 transition font-medium">
                Marketing Models
              </a>
              <a href="/join_and_earn" className="text-gray-300 hover:text-orange-500 transition font-medium">
                Join & Earn
              </a>

              <a href="/contact" className="text-gray-300 hover:text-orange-500 transition font-medium">
                Contact
              </a>
            </div>
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden md:block">
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 transition"
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition"
              >
                Get Started
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-white focus:outline-none"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-black/95 border-t border-gray-800">
          <div className="px-4 pt-2 pb-4 space-y-2">

            {/* ── Super Admin only: Dashboard link (Mobile) ── */}
            {isSuperAdmin && (
              <a
                href="/Superadmindashboard"
                className="flex items-center gap-2 px-3 py-2 text-orange-400 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 rounded-md font-semibold transition"
              >
                <LayoutDashboard className="w-4 h-4" />
                Admin Dashboard
              </a>
            )}

            <a
              href="/standard"
              className="block px-3 py-2 text-white hover:bg-gray-800 rounded-md font-medium transition"
            >
              Standards
            </a>
            <a
              href="/products"
              className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium transition"
            >
              Products
            </a>

            {/* Reference - Only show when logged in AND has access (Mobile) */}
            {isLoggedIn && hasRefAccess && (
              <a
                href="/reffrence"
                className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium transition"
              >
                Reference
              </a>
            )}

            {/* Courseware - Only show when logged in (Mobile) */}
            {isLoggedIn && (
              <a
                href="/courseware"
                className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium transition"
              >
                Courseware
              </a>
            )}

            <a
              href="/marketing"
              className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium transition"
            >
              Marketing Models
            </a>
            <a
              href="/join_and_earn"
              className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium transition"
            >
              Join & Earn
            </a>
            <a
              href="/contact"
              className="block px-3 py-2 text-gray-300 hover:bg-gray-800 rounded-md font-medium transition"
            >
              Contact
            </a>

            {/* Mobile CTA Button */}
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                className="w-full mt-4 px-6 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 transition"
              >
                Logout
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="w-full mt-4 px-6 py-2 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition"
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;